import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { TemplateState } from '../../../../types/common/types';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzTooltipWebComponent } from '../../../../ui/gzTooltip';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { GzCopyToClipboardWebComponent } from '../../../../ui/gzCopyToClipboard';
import { htmlEncode } from '../../../../utils/escape.utils';
import { ssoIdentityProviderLinks } from '../../../myCompany/my-company-constants';
import { UrlValidator } from '../../../../ui/gzForm/validators/url.validator';
import { MyCompanyAuthenticationData } from '../../../types/company-data';
import { RemoteValidator } from '../../../../ui/gzForm/validators/remote.validator';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { Skip2FAOptions } from '../../../types/skip-2FA-options';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { GzLinkWebComponent } from '../../../../ui/gzLinkComponent';

export class CompanyAuthenticationFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;

  public dom: {
    $wrapper: HTMLElement;

    $twoFactorAuthTitle: GzTitleWebComponent;
    $passwordExpiryOptionsTitle: GzTitleWebComponent;

    $twoFactorAuthCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $twoFactorAuthCheckboxInfoText: GzTextWebComponent;

    $skipTwoFactorAuthFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $skipTwoFactorAuthInfoText: GzTextWebComponent;

    $passwordAgeCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $passwordAgeCheckboxInfoText: GzTextWebComponent;

    $lockAccountCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $lockAccountCheckboxInfoText: GzTextWebComponent;

    $ssoSamlTitle: GzTitleWebComponent;
    $gravityZoneMetadataUrl: GzFormFieldWebComponent<GzInputWebComponent, string>;

    $ssoSamlIdentityProviderMetadata: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $ssoSamlKbLink: GzTextWebComponent;
    $gravityZoneSamlKbUrl: GzLinkWebComponent;
    $copyToClipboardGzMetadataUrl: GzCopyToClipboardWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.form-wrapper'),

      $twoFactorAuthTitle: this.shadow.querySelector('#twoFactorAuthTitle'),
      $passwordExpiryOptionsTitle: this.shadow.querySelector('#passwordExpiryOptionsTitle'),

      $twoFactorAuthCheckbox: this.shadow.querySelector('#twoFactorAuthCheckbox'),
      $twoFactorAuthCheckboxInfoText: this.shadow.querySelector('#twoFactorAuthCheckboxInfoText'),

      $skipTwoFactorAuthFormField: this.shadow.querySelector('#skipTwoFactorAuthFormField'),
      $skipTwoFactorAuthInfoText: this.shadow.querySelector('#skipTwoFactorAuthInfoText'),

      $passwordAgeCheckbox: this.shadow.querySelector('#passwordAgeCheckbox'),
      $passwordAgeCheckboxInfoText: this.shadow.querySelector('#passwordAgeCheckboxInfoText'),

      $lockAccountCheckbox: this.shadow.querySelector('#lockAccountCheckbox'),
      $lockAccountCheckboxInfoText: this.shadow.querySelector('#lockAccountCheckboxInfoText'),

      $ssoSamlTitle: this.shadow.querySelector('#ssoSamlTitle'),

      $gravityZoneMetadataUrl: this.shadow.querySelector('#gravityZoneMetadataUrl'),

      $ssoSamlIdentityProviderMetadata: this.shadow.querySelector('#ssoSamlIdentityProviderMetadata'),
      $ssoSamlKbLink: this.shadow.querySelector('#ssoSamlKbLink'),

      $gravityZoneSamlKbUrl: this.shadow.querySelector('#gravityZoneSamlKbUrl'),

      $copyToClipboardGzMetadataUrl: this.shadow.querySelector('#copyToClipboardGzMetadataUrl'),
    };

    this.dom.$gravityZoneSamlKbUrl.showIcon = false;

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.translations = this.getCompanyAuthenticationStrings();
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$twoFactorAuthTitle.textContent = this.translations.twoFactorAuthTitle;
    this.dom.$passwordExpiryOptionsTitle.textContent = this.translations.passwordExpiryOptionsTitle;
    this.dom.$twoFactorAuthCheckbox.Input.label = this.translations.twoFactorAuthLabel;
    this.dom.$twoFactorAuthCheckboxInfoText.innerHTML = this.translations.twoFactorAuthCheckboxInfoText;

    this.dom.$skipTwoFactorAuthFormField.label = this.translations.skipTwoFactorAuthLabel;
    this.dom.$skipTwoFactorAuthFormField.Input.placeholder = this.translations.skipTwoFactorAuthPlaceholder;
    this.dom.$skipTwoFactorAuthInfoText.innerHTML = this.translations.skipTwoFactorAuthInfoText;

    this.dom.$passwordAgeCheckbox.Input.label = this.translations.passwordAgeLabel;
    this.dom.$passwordAgeCheckboxInfoText.innerHTML = this.translations.passwordAgeAuthCheckboxInfoText;

    this.dom.$lockAccountCheckbox.Input.label = this.translations.lockAccountLabel;
    this.dom.$lockAccountCheckboxInfoText.innerHTML = this.translations.lockAccountAuthCheckboxInfoText;

    this.dom.$ssoSamlTitle.innerText = this.translations.ssoSamlTitle;
    this.dom.$ssoSamlKbLink.innerHTML = this.translations.ssoSamlKbLink;

    this.dom.$gravityZoneSamlKbUrl.innerHTML = this.translations.gravityZoneSamlKbUrlText;
    this.dom.$gravityZoneSamlKbUrl.href = this.translations.gravityZoneSamlKbUrl;

    this.dom.$gravityZoneMetadataUrl.label = this.translations.gravityZoneMetadataUrlLabel;
    this.dom.$ssoSamlIdentityProviderMetadata.label = this.translations.ssoSamlIdentityProviderMetadataLabel;
    this.dom.$ssoSamlIdentityProviderMetadata.Input.placeholder =
      this.translations.ssoSamlIdentityProviderMetadataPlaceholder;

    this.dom.$copyToClipboardGzMetadataUrl.confirmationMessage = this.commonStrings.copied;
  }

  public setFormValue(data: MyCompanyAuthenticationData) {
    this.dom.$twoFactorAuthCheckbox.value = data.specifics.twoFactorAuthentication
      ? data.specifics.twoFactorAuthentication.enforce
      : true;
    this.dom.$skipTwoFactorAuthFormField.Input.setFormValue(
      data.specifics.twoFactorAuthentication
        ? data.specifics.twoFactorAuthentication.skip2FAPeriod
        : Skip2FAOptions.NEVER
    );

    this.dom.$skipTwoFactorAuthFormField.initialValue = this.dom.$skipTwoFactorAuthFormField.value;

    this.dom.$passwordAgeCheckbox.value = data.specifics.enforcedPasswordLifetime;
    this.dom.$passwordAgeCheckbox.initialValue = this.dom.$passwordAgeCheckbox.value;
    this.dom.$lockAccountCheckbox.value = data.specifics.enforcePasswordLockdown ?? false;
    this.dom.$lockAccountCheckbox.initialValue = this.dom.$lockAccountCheckbox.value;

    if (data.specifics.identityProvider) {
      this.dom.$ssoSamlIdentityProviderMetadata.value = data.specifics.identityProvider.specifics.metadata;
    }

    this.formGroup?.validate(true);

    this.formGroup?.refreshInitialFormValue();
  }

  buildSuccessView() {
    this.createFormGroup();
    this.setSkipTwoFactorAuthDropdown();
    this.dom.$gravityZoneMetadataUrl.value = this.dependencies.getSsoDomain();
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private createFormGroup() {
    this.dom.$ssoSamlIdentityProviderMetadata.Validators = [
      UrlValidator(this.dependencies.translateFn('URL not valid')),
      RemoteValidator(
        (value: string) => this.dependencies.api.clients.Company.ValidateSamlUrl(value),
        (response: boolean) => response,
        this.buildSamlErrorMessage.bind(this),
        this.dependencies.translateFn('A communication error has occurred. Please enter the URL again')
      ),
    ];

    this.dom.$skipTwoFactorAuthFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$passwordAgeCheckbox.initialValue = false;
    this.dom.$lockAccountCheckbox.initialValue = false;

    this.formGroup = FormGroup.build({
      specifics: {
        twoFactorAuthentication: {
          skip2FAPeriod: this.dom.$skipTwoFactorAuthFormField,
        },
        identityProvider: {
          specifics: {
            metadata: this.dom.$ssoSamlIdentityProviderMetadata,
          },
        },
        enforcedPasswordLifetime: this.dom.$passwordAgeCheckbox,
        enforcePasswordLockdown: this.dom.$lockAccountCheckbox,
      },
    });
  }

  public setFormGroupToDisabled() {
    this.formGroup.setDisabled(true);

    this.dom.$gravityZoneMetadataUrl.disabled = true;
    this.dom.$ssoSamlIdentityProviderMetadata.disabled = true;
    this.dom.$copyToClipboardGzMetadataUrl.disabled = true;
  }

  protected onDisconnect() {
    this.formGroup?.unsubscribe();
  }

  private getCompanyAuthenticationStrings() {
    return {
      twoFactorAuthTitle: this.dependencies.translateFn('TWO-FACTOR AUTHENTICATION'),
      passwordExpiryOptionsTitle: this.dependencies.translateFn('PASSWORD EXPIRY OPTIONS'),
      twoFactorAuthLabel: this.dependencies.translateFn('Enforce two-factor authentication (Recommended)'),
      skipTwoFactorAuthLabel: this.dependencies.translateFn('Users trust their browsers'),
      skipTwoFactorAuthPlaceholder: this.dependencies.translateFn('Select period'),
      skipTwoFactorAuthInfoText: this.dependencies.translateFn('Users skip 2FA for the specified period.'),
      passwordAgeLabel: this.dependencies.translateFn('Set maximum password age to 90 days'),
      lockAccountLabel: this.dependencies.translateFn('Lock out account after 5 login attempts with invalid passwords'),
      ssoSamlTitle: this.dependencies.translateFn('SINGLE SIGN ON USING SAML'),
      ssoSamlKbLink: this.dependencies.translateFn('To configure single sign-on using SAML, please refer to'),
      gravityZoneSamlKbUrlText: `<span>${htmlEncode(this.dependencies.translateFn('KB article'))}</span>`,
      twoFactorAuthCheckboxInfoText: this.dependencies.translateFn(
        'Two-factor authentication is mandatory for all user accounts and you cannot disable it. At login, a configuration window will prompt users to enable this feature by downloading a two-factor authenticator app (compatible with RFC6238) and setting up their two-factor authentication. Users can skip enabling 2FA only five times.'
      ),
      passwordAgeAuthCheckboxInfoText: this.dependencies.translateFn(
        'This option enables the password expiration policy. The user needs to change their passwords sooner than the specified age. Otherwise, they will not be able to log in GravityZone anymore.'
      ),
      lockAccountAuthCheckboxInfoText: this.dependencies.translateFn(
        'This option limits the number of consecutive invalid passwords to prevent attacks. When the counter reaches the threshold, the account is locked out and the user needs to reset their password.'
      ),
      gravityZoneSamlKbUrl: ssoIdentityProviderLinks['default'],
      gravityZoneMetadataUrlLabel: this.dependencies.translateFn('GravityZone SAML metadata URL'),
      ssoSamlIdentityProviderMetadataLabel: this.dependencies.translateFn('Identity provider metadata URL'),
      ssoSamlIdentityProviderMetadataPlaceholder: this.dependencies.translateFn('Add URL'),
    };
  }

  private setSkipTwoFactorAuthDropdown() {
    toggleVisibility(this.dom.$skipTwoFactorAuthFormField, true);
    this.dom.$skipTwoFactorAuthFormField.disabled = false;
    const options = this.dependencies.getSkipTwoFactorAuthOptions();
    this.dom.$skipTwoFactorAuthFormField.Input.options = options.map(skip2FAOption => {
      return {
        title: skip2FAOption.name,
        value: skip2FAOption.value,
      };
    });
    this.dom.$skipTwoFactorAuthFormField.Input.setFormValue(Skip2FAOptions.SEVEN_DAYS);
    this.dom.$skipTwoFactorAuthFormField.initialValue = this.dom.$skipTwoFactorAuthFormField.value;
  }

  private buildSamlErrorMessage(response: unknown & { message?: string; exceptionType?: string }) {
    let exceptionErrorMessage;

    switch (response.exceptionType) {
      case 'Lib\\SSO\\Exceptions\\IdpMetadataXMLMissingEntityId':
        exceptionErrorMessage = this.dependencies.translateFn(
          'The identity provider metadata xml does not contain the entityId field.'
        );
        break;
      case 'Lib\\SSO\\Exceptions\\IdpMetadataXMLMissingSingleSignOnService':
        exceptionErrorMessage = this.dependencies.translateFn(
          'The identity provider metadata xml does not contain the singleSignOnService field.'
        );
        break;
      case 'Lib\\SSO\\Exceptions\\IdpMetadataXMLMissingX509':
        exceptionErrorMessage = this.dependencies.translateFn(
          'The identity provider metadata xml does not contain the x509cert field.'
        );
        break;
      case 'Lib\\SSO\\Exceptions\\IdpMetadataInvalidUrl':
        exceptionErrorMessage = this.dependencies.translateFn('The identity provider metadata xml url is invalid.');
        break;
      case 'Lib\\SSO\\Exceptions\\IdpMetadataXmlErrorParsingMetadata':
        exceptionErrorMessage = this.dependencies.translateFn(
          'An error was encountered while parsing the xml metadata.'
        );
        break;
      case 'Lib\\SSO\\Exceptions\\IdpMetadataXMLMissingIDPSSODescriptor':
        exceptionErrorMessage = this.dependencies.translateFn(
          'No IdP metadata found. Missing md:IDPSSODescriptor element from XML.'
        );
        break;
      case 'Exceptions\\ConnectFailed':
        exceptionErrorMessage = this.dependencies.translateFn('Could not connect to identity provider host.');
        break;
      case 'Lib\\SSO\\Exceptions\\IdpRemoteXmlRequestTimeOut':
        exceptionErrorMessage = this.dependencies.translateFn('Request to identity provider host timed out.');
        break;
      case 'Exceptions\\InvalidCertificate':
        exceptionErrorMessage = this.dependencies.translateFn(
          'Invalid certificate for the identity provider. GravityZone could not get metadata.'
        );
        break;
      default:
        exceptionErrorMessage = this.dependencies.translateFn(
          'A communication error has occurred. Please enter the URL again'
        );
    }

    return exceptionErrorMessage;
  }
}
